import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Package,
  PlusCircle,
  Search,
  Edit,
  Trash2,
  CheckSquare,
  Square,
  Archive,
  Eye,
  XCircle,
  ChevronDown,
  Layers,
  Star,
  Upload,
} from 'lucide-react';
import clsx from 'clsx';
import { useAdminProducts, useAdminCategories } from '../../services/adminService';
import { useBulkUpdateProducts } from '../../services/bulkService';
import { getImageUrl } from '../../lib/supabase';
import { formatPrice } from '../../lib/utils';
import { EditProductSlideOver } from '../../components/EditProductSlideOver/EditProductSlideOver';
import { DeleteProductModal } from '../../components/DeleteProductModal/DeleteProductModal';

/**
 * Admin products list page with edit slide-over, delete modal,
 * bulk selection, and archive toggle.
 */
export default function AdminProductsPage() {
  const { data: products, isLoading } = useAdminProducts();
  const { data: categories } = useAdminCategories();
  const bulkUpdate = useBulkUpdateProducts();

  const [search, setSearch] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [selected, setSelected] = useState(new Set());
  const [editProduct, setEditProduct] = useState(null);
  const [deleteProduct, setDeleteProduct] = useState(null);
  const [showBulkMenu, setShowBulkMenu] = useState(false);
  const [showCategoryPicker, setShowCategoryPicker] = useState(false);
  const [bulkConfirm, setBulkConfirm] = useState(null); // { action, label }

  // Filter: search + active/archived
  const filtered = (products || []).filter((p) => {
    const matchSearch = p.title.toLowerCase().includes(search.toLowerCase());
    const matchActive = showArchived ? p.is_active === false : p.is_active !== false;
    return matchSearch && matchActive;
  });

  // Selection helpers
  const toggleSelect = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const toggleAll = () => {
    if (selected.size === filtered.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(filtered.map((p) => p.id)));
    }
  };

  const clearSelection = () => setSelected(new Set());

  const handleBulkAction = (operation, value) => {
    if (selected.size === 0) return;
    bulkUpdate.mutate(
      { productIds: [...selected], operation, value },
      { onSuccess: () => { clearSelection(); setShowBulkMenu(false); setShowCategoryPicker(false); setBulkConfirm(null); } }
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-extrabold text-brand-dark">Products</h1>
          <p className="text-sm text-brand-gray mt-1">
            {filtered.length} {showArchived ? 'archived' : 'active'} product
            {filtered.length !== 1 ? 's' : ''}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Archive toggle */}
          <button
            onClick={() => {
              setShowArchived((v) => !v);
              clearSelection();
            }}
            className={clsx(
              'inline-flex items-center gap-2 px-4 py-2.5 text-sm font-bold rounded-xl transition-colors border',
              showArchived
                ? 'border-brand-red text-brand-red bg-brand-red/5 hover:bg-brand-red/10'
                : 'border-ui-border text-brand-gray hover:bg-brand-light'
            )}
          >
            {showArchived ? <Eye size={16} /> : <Archive size={16} />}
            {showArchived ? 'Show Active' : 'Archive'}
          </button>

          <Link
            to="/admin/import"
            className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-bold text-brand-gray border border-ui-border hover:bg-brand-light rounded-xl transition-colors"
          >
            <Upload size={16} />
            Import CSV
          </Link>

          <Link
            to="/admin/products/new"
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-brand-red hover:bg-red-700 text-brand-white text-sm font-bold rounded-xl transition-colors"
          >
            <PlusCircle size={16} />
            Add Product
          </Link>
        </div>
      </div>

      {/* Search + Bulk bar */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ui-muted" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search products..."
            className="w-full pl-9 pr-4 py-2.5 text-sm bg-brand-white border border-ui-border rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-red/20 placeholder:text-ui-muted"
          />
        </div>

        {/* Bulk actions bar */}
        <AnimatePresence>
          {selected.size > 0 && !showArchived && (
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: 20 }}
              className="flex items-center gap-2 flex-wrap"
            >
              <span className="text-sm font-bold text-brand-dark">
                {selected.size} selected
              </span>

              {/* Bulk actions dropdown */}
              <div className="relative">
                <button
                  onClick={() => { setShowBulkMenu((v) => !v); setShowCategoryPicker(false); }}
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-bold text-brand-dark border border-ui-border bg-brand-white hover:bg-brand-light rounded-xl transition-colors"
                >
                  Actions
                  <ChevronDown size={14} />
                </button>

                <AnimatePresence>
                  {showBulkMenu && (
                    <motion.div
                      initial={{ opacity: 0, y: -4 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -4 }}
                      className="absolute top-full left-0 mt-1 w-52 bg-brand-white border border-ui-border rounded-xl shadow-lg z-30 overflow-hidden"
                    >
                      <button
                        onClick={() => { setShowCategoryPicker(true); setShowBulkMenu(false); }}
                        className="flex items-center gap-2 w-full px-4 py-2.5 text-sm text-brand-dark hover:bg-brand-light transition-colors text-left"
                      >
                        <Layers size={14} className="text-brand-gray" />
                        Change Category
                      </button>
                      <button
                        onClick={() => setBulkConfirm({ action: 'featured_on', label: 'Mark as Featured' })}
                        className="flex items-center gap-2 w-full px-4 py-2.5 text-sm text-brand-dark hover:bg-brand-light transition-colors text-left"
                      >
                        <Star size={14} className="text-warning" />
                        Mark as Featured
                      </button>
                      <button
                        onClick={() => setBulkConfirm({ action: 'featured_off', label: 'Remove Featured' })}
                        className="flex items-center gap-2 w-full px-4 py-2.5 text-sm text-brand-dark hover:bg-brand-light transition-colors text-left"
                      >
                        <Star size={14} className="text-brand-gray" />
                        Remove Featured
                      </button>
                      <div className="border-t border-ui-border" />
                      <button
                        onClick={() => setBulkConfirm({ action: 'delete', label: 'Delete Selected' })}
                        className="flex items-center gap-2 w-full px-4 py-2.5 text-sm text-error hover:bg-red-50 transition-colors text-left"
                      >
                        <Trash2 size={14} />
                        Delete Selected
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Category picker */}
              <AnimatePresence>
                {showCategoryPicker && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    className="flex items-center gap-2"
                  >
                    <select
                      onChange={(e) => {
                        if (e.target.value) handleBulkAction('change_category', e.target.value);
                      }}
                      defaultValue=""
                      className="px-3 py-2 text-sm bg-brand-white border border-ui-border rounded-xl focus:outline-none focus:ring-2 focus:ring-brand-red/20"
                    >
                      <option value="" disabled>Select category…</option>
                      {(categories || []).map((c) => (
                        <option key={c.id} value={c.id}>{c.name}</option>
                      ))}
                    </select>
                    <button
                      onClick={() => setShowCategoryPicker(false)}
                      className="p-1.5 rounded-lg text-brand-gray hover:bg-brand-light"
                    >
                      <XCircle size={16} />
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>

              <button
                onClick={clearSelection}
                className="p-2 rounded-lg text-brand-gray hover:bg-brand-light transition-colors"
              >
                <XCircle size={16} />
              </button>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Product list */}
      <div className="bg-brand-white border border-ui-border rounded-2xl overflow-hidden">
        {isLoading ? (
          <div className="p-8 space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="flex items-center gap-4 animate-pulse">
                <div className="w-14 h-14 bg-brand-light rounded-xl flex-shrink-0" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 bg-brand-light rounded w-1/3" />
                  <div className="h-3 bg-brand-light rounded w-1/4" />
                </div>
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center">
            <Package size={40} className="text-ui-muted mx-auto mb-3" />
            <p className="text-sm font-medium text-brand-gray">
              {showArchived ? 'No archived products' : 'No products found'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-ui-border">
            {/* Select all row */}
            {!showArchived && filtered.length > 0 && (
              <div className="flex items-center gap-3 px-4 py-2.5 bg-ui-background">
                <button onClick={toggleAll} className="text-brand-gray hover:text-brand-dark transition-colors">
                  {selected.size === filtered.length ? (
                    <CheckSquare size={18} className="text-brand-red" />
                  ) : (
                    <Square size={18} />
                  )}
                </button>
                <span className="text-xs font-bold text-brand-gray uppercase tracking-wide">
                  Select all
                </span>
              </div>
            )}

            <AnimatePresence mode="popLayout">
              {filtered.map((product, i) => (
                <motion.div
                  key={product.id}
                  layout
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0, x: -40, transition: { duration: 0.2 } }}
                  transition={{ delay: i * 0.02 }}
                  className={clsx(
                    'flex items-center gap-4 p-4 transition-colors',
                    selected.has(product.id)
                      ? 'bg-brand-red/5'
                      : 'hover:bg-brand-light/50'
                  )}
                >
                  {/* Checkbox (only for active products) */}
                  {!showArchived && (
                    <button
                      onClick={() => toggleSelect(product.id)}
                      className="text-brand-gray hover:text-brand-dark transition-colors flex-shrink-0"
                    >
                      {selected.has(product.id) ? (
                        <CheckSquare size={18} className="text-brand-red" />
                      ) : (
                        <Square size={18} />
                      )}
                    </button>
                  )}

                  <img
                    src={getImageUrl(product.image_path)}
                    alt={product.title}
                    className="w-14 h-14 rounded-xl object-cover flex-shrink-0 bg-brand-light"
                  />
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-bold text-brand-dark truncate">
                      {product.title}
                    </h3>
                    <p className="text-xs text-brand-gray mt-0.5">
                      {product.categories?.name || 'Uncategorized'}
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-sm font-extrabold text-brand-dark">
                      {formatPrice(product.price)}
                    </p>
                    {product.is_featured && (
                      <span className="text-[10px] font-bold text-brand-red bg-brand-red/10 px-1.5 py-0.5 rounded">
                        Featured
                      </span>
                    )}
                  </div>

                  {/* Actions */}
                  {!showArchived && (
                    <div className="flex items-center gap-1 flex-shrink-0">
                      <button
                        onClick={() => setEditProduct(product)}
                        className="p-2 rounded-lg text-brand-gray hover:bg-blue-50 hover:text-blue-600 transition-colors"
                        aria-label="Edit"
                      >
                        <Edit size={16} />
                      </button>
                      <button
                        onClick={() => setDeleteProduct(product)}
                        className="p-2 rounded-lg text-brand-gray hover:bg-red-50 hover:text-error transition-colors"
                        aria-label="Delete"
                      >
                        <Trash2 size={16} />
                      </button>
                    </div>
                  )}
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>

      {/* Edit slide-over */}
      <EditProductSlideOver
        product={editProduct}
        onClose={() => setEditProduct(null)}
      />

      {/* Delete confirmation modal */}
      <DeleteProductModal
        product={deleteProduct}
        onClose={() => setDeleteProduct(null)}
        onDeleted={() => setDeleteProduct(null)}
      />

      {/* Bulk confirm modal */}
      <AnimatePresence>
        {bulkConfirm && (
          <>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => { setBulkConfirm(null); setShowBulkMenu(false); }}
              className="fixed inset-0 bg-brand-dark/60 backdrop-blur-sm z-50"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="fixed z-50 top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full max-w-sm bg-brand-white rounded-2xl shadow-2xl p-6"
            >
              <h3 className="text-lg font-bold text-brand-dark mb-2">
                {bulkConfirm.label}
              </h3>
              <p className="text-sm text-brand-gray mb-5">
                This will affect <span className="font-bold text-brand-dark">{selected.size}</span> product{selected.size !== 1 ? 's' : ''}. Are you sure?
              </p>
              <div className="flex items-center gap-3">
                <button
                  onClick={() => {
                    if (bulkConfirm.action === 'delete') handleBulkAction('delete');
                    else if (bulkConfirm.action === 'featured_on') handleBulkAction('toggle_featured', true);
                    else if (bulkConfirm.action === 'featured_off') handleBulkAction('toggle_featured', false);
                  }}
                  disabled={bulkUpdate.isPending}
                  className={clsx(
                    'flex-1 py-2.5 text-sm font-bold rounded-xl transition-colors text-brand-white',
                    bulkConfirm.action === 'delete'
                      ? 'bg-error hover:bg-red-700'
                      : 'bg-brand-red hover:bg-red-700'
                  )}
                >
                  {bulkUpdate.isPending ? 'Processing…' : 'Confirm'}
                </button>
                <button
                  onClick={() => { setBulkConfirm(null); setShowBulkMenu(false); }}
                  className="flex-1 py-2.5 text-sm font-bold text-brand-gray bg-brand-light hover:bg-gray-200 rounded-xl transition-colors"
                >
                  Cancel
                </button>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
}
